import eventBus from "@sd-utility/eventbus";
import { Button } from "@tvg/fuse-design/buttons";
import { Modal as ModalFuse } from "@tvg/fuse-design/modal";
import { gridConfig } from "@tvg/fuse-design/responsive-grid";
import { TextBlock } from "@tvg/fuse-design/text-block";
import { IconChevronRight } from "@tvg/fuse-icons";
import useRender from "client/hooks/useRender";
import getEventHandler from "client/utils/eventHandler";
import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import { AmpModalConfig } from "types/amplience";

const backgroundConfig: { [key: string]: string } = {
  "Brand Light": "brandLight",
  "Brand Mid": "brandMid",
  Grey: "grey",
  Black: "black",
};

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 var(--spacing-global-space5);
  margin-bottom: var(--spacing-global-space5);

  @media only screen and (min-width: ${gridConfig.m.minWidth}) {
    padding: 0 var(--spacing-global-space6);
    margin-bottom: var(--spacing-global-space6);
  }

  @media only screen and (min-width: ${gridConfig.l.minWidth}) {
    padding: 0 var(--spacing-global-space7);
    margin-bottom: var(--spacing-global-space7);
  }

  > button,
  > a {
    @media only screen and (max-width: ${gridConfig.m.minWidth}) {
      width: 100%;
    }
  }
`;

const StyledTextBlock = styled(TextBlock)`
  && {
    > *:last-child {
      margin-bottom: 0;
    }
  }
`;

export interface ModalProps {
  config: AmpModalConfig;
}

export const Modal: React.FC<ModalProps> = ({ config: { modalKey, header, content } }) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const render = useRender();

  useEffect(() => {
    const listener = eventBus.subscribe<string>("modal/open", (eventData) => {
      eventData === modalKey && setModalOpen(true);
    });
    return listener.unsubscribe;
  }, [modalKey]);

  const handleClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleCTAClick = useMemo(() => {
    if (!content.cta?.publishEvent) return;

    return getEventHandler(content.cta.publishEvent);
  }, [content.cta?.publishEvent]);

  const headlineHTML = { __html: !content.headline?.headlineText ? "" : render(content.headline.headlineText) };

  const mainContentHTML = {
    __html: !content.mainContent?.mainContentText ? "" : render(content.mainContent.mainContentText),
  };

  return (
    <ModalFuse
      className={modalKey.replace("modal/", "modal-")}
      headerIcon={header.icon}
      dataTestId="content-modal"
      title={header.title}
      hasPadding={false}
      srLabelCloseButton="Close modal"
      zIndex="99" // Setting this value initially to work with Search and Menu which have z-index of 100
      open={modalOpen}
      onClose={handleClose}
    >
      {content.headline && content.headline.headlineText && (
        <StyledTextBlock
          background={content.headline.background ? backgroundConfig[content.headline.background] : undefined}
        >
          <div dangerouslySetInnerHTML={headlineHTML} />
        </StyledTextBlock>
      )}

      {content.mainContent && content.mainContent.mainContentText && (
        <StyledTextBlock>
          <div dangerouslySetInnerHTML={mainContentHTML} />
        </StyledTextBlock>
      )}

      {content.cta && (content.cta.url || content.cta.publishEvent) && (
        <StyledButtonContainer>
          <Button href={content.cta.url} iconRight icon={<IconChevronRight />} onClick={handleCTAClick}>
            {content.cta.text}
          </Button>
        </StyledButtonContainer>
      )}
    </ModalFuse>
  );
};
